// src/ui/TrackPanel.js
//
// What a person can do to one track: name it, colour it, move it
// up or down, delete it. It lives in the dock beside the track's chain, so the
// header stays free for the controls used during playing.
//
// Colour is always a second cue. A track's name is shown wherever its colour
// is, and the panel says the colour's name in words.
//
// A control that cannot act is left out, not disabled: the first track has no
// Move up and the last no Move down.

import { MAX_FOLDER_NAME } from '../model/EditorState.js'
import { createAmountField } from './AmountField.js'

export const COLORS = Object.freeze([
  { name: 'Red', value: '#e5484d' },
  { name: 'Orange', value: '#f5a524' },
  { name: 'Yellow', value: '#e2c541' },
  { name: 'Green', value: '#46a758' },
  { name: 'Teal', value: '#12a594' },
  { name: 'Blue', value: '#3e8ef7' },
  { name: 'Purple', value: '#8e4ec6' },
  { name: 'Pink', value: '#e93d82' }
])


export function colorName (value) {
  return COLORS.find(c => c.value === value)?.name ?? (value ? value : 'None')
}

export function createTrackPanel (document, { onRename, onColor, onFolder, onMove, onDelete, onFreeze, onGroupLevel = null, onGroupPan = null, onExportMidi = null }) {
  for (const [name, fn] of Object.entries({ onRename, onColor, onFolder, onMove, onDelete, onFreeze })) {
    if (typeof fn !== 'function') throw new Error(`createTrackPanel needs ${name}`)
  }
  const element = document.createElement('div')
  element.className = 'track-panel'
  let trackId = null

  // Name
  const nameLabel = document.createElement('label')
  nameLabel.className = 'track-field'
  nameLabel.append(Object.assign(document.createElement('span'), { textContent: 'Name' }))
  const name = document.createElement('input')
  name.type = 'text'
  name.id = 'track-name-input'
  name.autocomplete = 'off'
  name.spellcheck = false
  const commit = () => onRename(trackId, name.value.trim() === '' ? null : name.value.trim())
  name.addEventListener('change', commit)
  name.addEventListener('keydown', event => { if (event.key === 'Enter') { event.preventDefault(); commit() } })
  nameLabel.append(name)

  // Folder: a name, shared by the tracks that belong together. Empty takes the track out of its folder.
  const folderLabel = document.createElement('label')
  folderLabel.className = 'track-field'
  folderLabel.append(Object.assign(document.createElement('span'), { textContent: 'Folder' }))
  const folder = document.createElement('input')
  folder.type = 'text'
  folder.id = 'track-folder-input'
  folder.autocomplete = 'off'
  folder.spellcheck = false
  folder.maxLength = MAX_FOLDER_NAME
  folder.setAttribute('list', 'track-folder-names')
  const folderNames = document.createElement('datalist')
  folderNames.id = 'track-folder-names'
  const commitFolder = () => onFolder(trackId, folder.value.trim() === '' ? null : folder.value.trim())
  folder.addEventListener('change', commitFolder)
  folder.addEventListener('keydown', event => { if (event.key === 'Enter') { event.preventDefault(); commitFolder() } })
  folderLabel.append(folder, folderNames)

  // Colour
  const colorGroup = document.createElement('div')
  colorGroup.className = 'track-colors'
  colorGroup.setAttribute('role', 'group')
  colorGroup.setAttribute('aria-label', 'Colour')
  const colorCaption = document.createElement('span')
  colorCaption.className = 'track-caption'
  colorCaption.setAttribute('aria-hidden', 'true')
  colorCaption.textContent = 'Colour'
  colorGroup.append(colorCaption)
  const colorSaid = document.createElement('span')
  colorSaid.className = 'track-color-said'
  const swatches = [{ name: 'None', value: null }, ...COLORS].map(color => {
    const button = document.createElement('button')
    button.type = 'button'
    button.className = 'swatch'
    button.dataset.color = color.value ?? ''
    button.setAttribute('aria-label', color.name)
    button.setAttribute('aria-pressed', 'false')
    if (color.value) button.style.background = color.value
    else button.textContent = '×'
    button.addEventListener('click', () => onColor(trackId, color.value))
    colorGroup.append(button)
    return button
  })
  colorGroup.append(colorSaid)

  // Order and delete
  const up = document.createElement('button')
  up.type = 'button'
  up.id = 'track-move-up'
  up.textContent = 'Move up'
  up.addEventListener('click', () => onMove(trackId, -1))
  const down = document.createElement('button')
  down.type = 'button'
  down.id = 'track-move-down'
  down.textContent = 'Move down'
  down.addEventListener('click', () => onMove(trackId, 1))
  const position = document.createElement('span')
  position.className = 'track-position'
  const remove = document.createElement('button')
  remove.type = 'button'
  remove.id = 'track-delete'
  remove.className = 'danger'
  remove.addEventListener('click', () => onDelete(trackId))

  // Render the track as heard to an audio clip on a new track, and mute this one (web/app/Bounce.js).
  const freeze = document.createElement('button')
  freeze.type = 'button'
  freeze.id = 'track-freeze'
  freeze.textContent = 'Freeze track'
  freeze.title = 'Render this track as heard to an audio clip on a new track, and mute this one'
  freeze.addEventListener('click', () => onFreeze(trackId))

  // A bus or folder's own level for its members: each moves by the same decibels (src/model/GroupChannel.js).
  // Left out unless the track has members, and unless the host can act on it.
  const group = document.createElement('div')
  group.className = 'track-actions'
  group.setAttribute('role', 'group')
  group.id = 'track-group-level'
  const groupStep = (id, text, decibels) => {
    const button = document.createElement('button')
    button.type = 'button'
    button.id = id
    button.textContent = text
    button.addEventListener('click', () => onGroupLevel(trackId, decibels))
    return button
  }
  // The same for pan: a shift for every member, stopping where the one nearest the edge arrives.
  const panStep = (id, text, amount) => {
    const button = document.createElement('button')
    button.type = 'button'
    button.id = id
    button.textContent = text
    button.addEventListener('click', () => onGroupPan(trackId, amount))
    return button
  }
  if (onGroupPan) {
    group.append(panStep('track-group-pan-left', 'Members pan left 0.1', -0.1), panStep('track-group-pan-right', 'Members pan right 0.1', 0.1))
    group.append(createAmountField(document, { id: 'track-group-pan-by', label: 'Members pan by', unit: '(-1 left to 1 right)', min: -1, max: 1, step: 0.05, onApply: amount => onGroupPan(trackId, amount) }).element)
  }
  if (onGroupLevel) {
    group.append(groupStep('track-group-down', 'Members down 1 dB', -1), groupStep('track-group-up', 'Members up 1 dB', 1))
    group.append(createAmountField(document, { id: 'track-group-by', label: 'Members by', unit: 'dB', min: -60, max: 24, step: 0.5, onApply: decibels => onGroupLevel(trackId, decibels) }).element)
  }
  group.hidden = true

  // Save this track's MIDI notes and controller events to a file. Left out for a track with none, or a host that cannot.
  const exportMidi = document.createElement('button')
  exportMidi.type = 'button'
  exportMidi.id = 'track-export-midi'
  exportMidi.textContent = 'Export track as MIDI'
  exportMidi.title = 'Save the notes of this track\'s MIDI clips to a Standard MIDI file'
  exportMidi.addEventListener('click', () => onExportMidi(trackId))
  exportMidi.hidden = true

  const actions = document.createElement('div')
  actions.className = 'track-actions track-main-actions'
  actions.append(position, up, down, freeze, ...(onExportMidi ? [exportMidi] : []), remove)
  // Name and folder share one row, wrapping on a narrow dock; the actions follow, with Delete at the far end.
  const fields = document.createElement('div')
  fields.className = 'track-fields'
  fields.append(nameLabel, folderLabel)
  element.append(fields, colorGroup, group, actions)

  return {
    element,
    get trackId () { return trackId },
    /**
     * `defaultName` is what the track is called with no name of its own,
     * `plugins` how many a delete would take with it, `index` and `count` where
     * it stands in the arrangement.
     */
    show ({ id, name: current, defaultName, layout, index, count, plugins, folders = [], members = 0, hasMidi = false }) {
      trackId = id
      // A name being typed is not overwritten by a redraw.
      if (document.activeElement !== name) name.value = current ?? ''
      name.placeholder = defaultName
      if (document.activeElement !== folder) folder.value = layout.folder ?? ''
      folderNames.replaceChildren(...folders.map(text => { const option = document.createElement('option'); option.value = text; return option }))
      colorSaid.textContent = ` ${colorName(layout.color)}`
      for (const button of swatches) button.setAttribute('aria-pressed', String((button.dataset.color || null) === layout.color))
      group.hidden = !onGroupLevel || members === 0
      exportMidi.hidden = !hasMidi
      group.setAttribute('aria-label', `Move the ${members} tracks in this bus or folder together`)
      position.textContent = `Track ${index + 1} of ${count}.`
      up.hidden = index === 0
      down.hidden = index === count - 1
      remove.textContent = plugins > 0 ? `Delete track and ${plugins === 1 ? '1 plugin' : `${plugins} plugins`}` : 'Delete track'
      remove.setAttribute('aria-label', `${remove.textContent}: ${defaultName}. Undo brings it back.`)
    }
  }
}
